import React from "react";
import {useForm, Controller} from "react-hook-form";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import {
  useNavigate,
  Link,
  useLocation,
  useSearchParams,
} from "react-router-dom";

import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import FormLabel from "@mui/material/FormLabel";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import RadioGroup from "@mui/material/RadioGroup";
import Radio from "@mui/material/Radio";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import {singleSelectDropdownStyles} from "components/ReportComponents/singleSelectDropdownStyles";
import {CPSConstants, CPSNumbers, PageNames} from "commonUtils/enumerations";
import {getRequest, setHeader} from "repository/genericRepository";
import {FACULTY_BASIC_SEARCH_CONTROLS} from "commonUtils/globalAPISettings";
import {store} from "../../../redux/store";
import {isValidObject, loggedMessage} from "commonUtils/utils";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import {facultyStyle} from "./facultyStyle";
import {FormButton} from "components/FormComponents/Button/FormButton";
import {
  capitalizeFirstLetter,
  handleAxiosErrors,
  type BasicSearchFormValues,
} from "./facultyUtils";
import type {BrandsProps, StatesProps} from "commonUtils/genericInterfaces";

const FacultyBasicSearch = () => {
  const navigate = useNavigate();
  const {register, handleSubmit, control, setValue, reset} =
    useForm<BasicSearchFormValues>({
      defaultValues: {
        lastName: "",
        firstName: "",
        facultyId: "",
        city: "",
        stateId: CPSNumbers.MINUS_ONE,
        brandId: CPSNumbers.MINUS_ONE,
        contractedId: CPSNumbers.MINUS_ONE,
        facultyTypeIds: "1",
      },
    });
  const [errorMsg, setErrorMsg] = React.useState<string>("");
  const [states, setStates] = React.useState<StatesProps[]>([]);
  const [brands, setBrands] = React.useState<BrandsProps[]>([]);
  const location = useLocation();
  let [searchParams] = useSearchParams();
  const FACULTY_ROUTE = "/faculty/";
  const ACTIVITY_ROUTE = "/activity/";

  React.useEffect(() => {
    try {
      FacultyBasicSearchControl();
    } catch (error: any) {
      loggedMessage(error, errorMsg, PageNames.FACULTY);
    }
  }, []);

  React.useEffect(() => {
    if (isValidObject(location?.state?.params)) {
      searchParams = new URLSearchParams(location?.state?.params);
    }
    let submenuid = 0;
    if (
      isValidObject(
        searchParams.get("subMenuId") ?? searchParams.get("submenuid"),
      )
    ) {
      submenuid = Number(
        searchParams.get("subMenuId") ?? searchParams.get("submenuid"),
      );
    }
    if (submenuid === 14) {
      reset();
    }
  }, [location.pathname]);

  const onActionChange = (event: any) => {
    try {
      setValue("contractedId", Number(event?.target.value));
    } catch (error: any) {
      loggedMessage(error, errorMsg, PageNames.FACULTY);
    }
  };

  const FacultyBasicSearchControl = React.useMemo(
    () => async () => {
      try {
        const authState = store.getState();
        const APIHeaders = {
          Authorization: `Bearer ${authState.auth?.authDetails?.token}`,
        };
        const supplierId = authState.auth?.userDetails?.supplierId;
        const responseData = await getRequest({
          url: `${FACULTY_BASIC_SEARCH_CONTROLS}?supplierId=${supplierId}`,
          headers: setHeader(APIHeaders),
          params: {},
        });
        if (isValidObject(responseData)) {
          setStates(responseData?.states);
          setBrands(responseData?.brands);
        }
      } catch (error: any) {
        loggedMessage(error, errorMsg, PageNames.FACULTY);
        handleAxiosErrors(error, setErrorMsg);
      }
    },
    [states, brands],
  );

  const GetFaculySearchResult = (data: BasicSearchFormValues) => {
    try {
      if (window?.location?.pathname?.includes("activity")) {
        navigate("/activity/facultybasicsearchresults", {
          state: data,
        });
      } else {
        navigate("/faculty/facultybasicsearchresults", {
          state: data,
        });
      }
    } catch (error: any) {
      loggedMessage(error, errorMsg, PageNames.FACULTY);
    }
  };

  const changeAdvanceSearchURL = () => {
    let pathName = location.pathname;

    let url = "";

    if (pathName?.includes(FACULTY_ROUTE)) {
      url = "/faculty/facultyadvancedsearch";
    } else if (pathName?.includes(ACTIVITY_ROUTE)) {
      url = "/activity/facultyadvancedsearch";
    }

    return url;
  };

  return (
    <>
      <Box
        component="form"
        onSubmit={(event: React.FormEvent<HTMLFormElement>) => {
          event.preventDefault();
          handleSubmit(GetFaculySearchResult)(event);
        }}
        noValidate
        sx={{
          ...facultyStyle.facultyBasicSearchContainer,
          ...facultyStyle.facultyBoxStyle,
        }}>
        <Box sx={facultyStyle.facultyBasicBoxContainer}>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <InputLabel sx={singleSelectDropdownStyles.muilabel}>
                Last Name
              </InputLabel>
              <TextField
                className="input-font-family faculty-text-field"
                {...register("lastName")}
                id="lastName"
                name="lastName"
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <InputLabel sx={singleSelectDropdownStyles.muilabel}>
                First Name
              </InputLabel>
              <TextField
                className="input-font-family faculty-text-field"
                {...register("firstName")}
                id="firstName"
                name="firstName"
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <InputLabel sx={singleSelectDropdownStyles.muilabel}>
                Faculty Key
              </InputLabel>
              <TextField
                className="input-font-family faculty-text-field"
                {...register("facultyId")}
                id="facultyId"
                name="facultyId"
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <InputLabel sx={singleSelectDropdownStyles.muilabel}>
                City
              </InputLabel>
              <Controller
                name="city"
                control={control}
                render={({field}) => (
                  <TextField
                    className="input-font-family faculty-text-field"
                    {...field}
                    id="city"
                    name="city"
                    size="small"
                    onChange={(event: any) => {
                      field.onChange(
                        capitalizeFirstLetter(event.target.value, errorMsg),
                      );
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <InputLabel sx={singleSelectDropdownStyles.muilabel}>
                State
              </InputLabel>
              <Select
                {...register("stateId")}
                sx={facultyStyle.searchStatedropdown}
                className="search-basic-select-box"
                size="small"
                placeholder=""
                defaultValue={CPSNumbers.MINUS_ONE}
                MenuProps={{
                  disablePortal: true,
                  anchorOrigin: {
                    vertical: "center",
                    horizontal: "left",
                  },
                }}>
                {states?.map(
                  (row: {id: number; name: string}, index: number) => (
                    <MenuItem
                      className="white-space main-font-family"
                      key={index}
                      value={row.id}>
                      {row.name}
                    </MenuItem>
                  ),
                )}
              </Select>
            </Grid>
            <Grid item xs={12}>
              <InputLabel sx={singleSelectDropdownStyles.muilabel}>
                <Box>Brands/Products ,</Box>
                <Box>Indication/Disease State</Box>
              </InputLabel>
              <Select
                {...register("brandId")}
                sx={facultyStyle.searchStatedropdown}
                className="search-basic-select-box"
                size="small"
                placeholder=""
                defaultValue={CPSNumbers.MINUS_ONE}
                MenuProps={{
                  disablePortal: true,
                  anchorOrigin: {
                    vertical: "center",
                    horizontal: "left",
                  },
                }}>
                {brands?.map(
                  (row: {id: number; brandName: string}, index: number) => (
                    <MenuItem
                      className="white-space main-font-family"
                      key={index}
                      value={row.id}>
                      {row.brandName}
                    </MenuItem>
                  ),
                )}
              </Select>
            </Grid>
            <Grid item xs={12}>
              <FormLabel sx={singleSelectDropdownStyles.muilabel}>
                Contracted
              </FormLabel>
              <RadioGroup
                row
                aria-labelledby="demo-radio-buttons-group-label"
                defaultValue={CPSNumbers.MINUS_ONE}
                name="radio-buttons-group"
                onChange={event => {
                  onActionChange(event);
                }}>
                <FormControlLabel
                  className="form-control-label"
                  value={1}
                  control={<Radio />}
                  label="Yes"
                />
                <FormControlLabel
                  className="form-control-label"
                  value={0}
                  control={<Radio />}
                  label="No"
                />
                <FormControlLabel
                  className="form-control-label"
                  value={CPSNumbers.MINUS_ONE}
                  control={<Radio />}
                  label="All"
                />
              </RadioGroup>
            </Grid>
            <Grid item xs={12}>
              <FormLabel sx={singleSelectDropdownStyles.muilabel}>
                Faculty Type
              </FormLabel>
              <FormGroup
                aria-labelledby="demo-radio-buttons-group-label"
                defaultValue="1"
                style={{flexDirection: "row"}}>
                <FormControlLabel
                  className="form-control-label"
                  control={
                    <Checkbox
                      value={CPSNumbers.ONE}
                      {...register("facultyTypeIds")}
                      defaultChecked
                    />
                  }
                  label="HCP"
                />
                <FormControlLabel
                  className="form-control-label"
                  control={
                    <Checkbox
                      value={CPSNumbers.THREE}
                      {...register("facultyTypeIds")}
                      defaultChecked
                    />
                  }
                  label="Non-HCP"
                />
                <FormControlLabel
                  className="form-control-label"
                  control={
                    <Checkbox
                      value={CPSNumbers.TWO}
                      {...register("facultyTypeIds")}
                      defaultChecked
                    />
                  }
                  label="Patient/Caregiver"
                />
              </FormGroup>
            </Grid>
            <Grid item xs={12}>
              <FormButton
                variant="contained"
                size="small"
                type="submit"
                outlet="Search Faculty"
              />
              <Link to={changeAdvanceSearchURL()}>
                <Box sx={facultyStyle.advanceSearchLink}>
                  <KeyboardDoubleArrowRightIcon />
                  Advanced Search
                </Box>
              </Link>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </>
  );
};

export default FacultyBasicSearch;
